import { CompanyAutocompleteField, DEFAULT_OPTION, useAppForm } from '@massilia/features'
import { useAddMyFidelityCardMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { toast } from 'sonner'

import { resolveAuthErrorMessage } from '@/lib/auth-error-message'
import { fidelityCardSchema } from '@/lib/schemas/personal-informations/personal-informations.schemas'

interface FidelityCardFormProps {
  passengerId: string
  onClose: () => void
}

export function FidelityCardForm({ passengerId, onClose }: FidelityCardFormProps) {
  const { mutateAsync: addFidelityCard } = useAddMyFidelityCardMutation()

  const form = useAppForm({
    defaultValues: {
      cardNumber: '',
      company: DEFAULT_OPTION,
    },
    onSubmit: async ({ value }) => {
      try {
        await addFidelityCard({
          companyId: value.company.value,
          passengerId,
          cardNumber: value.cardNumber,
        })
        onClose()
      } catch (error) {
        toast.error(resolveAuthErrorMessage(error))
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate({ validationSource: 'form', value }, fidelityCardSchema),
    },
  })

  return (
    <form
      className="mt-3 flex flex-col gap-3 rounded-lg border border-slate-200 p-3"
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <form.AppField
        name="company"
        children={() => <CompanyAutocompleteField label="Compagnie" valueField="id" />}
      />
      <form.AppField
        name="cardNumber"
        children={(field) => (
          <field.TextField label="Numéro de carte" placeholder="Numéro de carte de fidélité" />
        )}
      />
      <div className="flex gap-2">
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <>
              <Button
                className="bg-brand-600 hover:bg-brand-700 text-white"
                disabled={isSubmitting}
                size="sm"
                type="submit"
              >
                {isSubmitting ? 'Ajout...' : 'Ajouter'}
              </Button>
              <Button
                disabled={isSubmitting}
                onClick={onClose}
                size="sm"
                type="button"
                variant="outline"
              >
                Annuler
              </Button>
            </>
          )}
        </form.Subscribe>
      </div>
    </form>
  )
}
